import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import classNames from "classnames"

import { ProductConfigurationModelDimInfo } from "shared/types/__generated__/graphql"
import { Button, ButtonVariant } from "shared/ui/buttons/Button"
import { Hint } from "shared/ui/hint/Hint"
import { IconName } from "shared/ui/icon/__generated__/IconName"
import { LoadingSpinner } from "shared/ui/loading-spinner/LoadingSpinner"

import { ExternalProductDimensioningProps } from "./ExternalProductDimensioning"

interface ExternalProductDimensioningViewModel
  extends ExternalProductDimensioningProps {
  renderBaseProduct?: (
    dimInfo: ProductConfigurationModelDimInfo
  ) => React.ReactNode
}

export const ExternalProductDimensioningView = React.memo(
  ({
    externalDimensioningResult,
    isExternalProductDimensioningAvailable,
    externalProductDimensioningURLs,
    className,
    reset,
    renderBaseProduct
  }: ExternalProductDimensioningViewModel) => {
    const { t } = useTranslation()
    const [isLoading, setIsLoading] = useState(true)

    const [selectedURL, setSelectedURL] = useState<string | null>(
      externalProductDimensioningURLs.length === 1
        ? externalProductDimensioningURLs[0].url
        : null
    )

    useEffect(() => {
      if (externalProductDimensioningURLs.length === 1) {
        setSelectedURL(externalProductDimensioningURLs[0].url)
      }
    }, [externalProductDimensioningURLs])

    if (!isExternalProductDimensioningAvailable) {
      return null
    }

    if (externalDimensioningResult) {
      return (
        <div className={classNames("external-product-dimensioning", className)}>
          <div className="external-product-dimensioning__complete card card--elevated">
            <div className="external-product-dimensioning__info">
              {t("Product.Configuration.ChangeDimensioningExplanation")}
            </div>
            <Button
              variant={ButtonVariant.PRIMARY}
              className="external-product-dimensioning__reset-button"
              onClick={reset}
            >
              {t("Product.Configuration.ChangeDimensioning")}
            </Button>
          </div>
        </div>
      )
    }

    if (externalProductDimensioningURLs.length === 0) {
      return (
        <div className={classNames("external-product-dimensioning", className)}>
          <Hint.Root className="hint--banner hint--danger external-product-dimensioning__hint">
            <Hint.Icon name={IconName.WARNING} />
            <Hint.Content>
              <Hint.Title>
                {t("Product.Configuration.NoDimensioningURL")}
              </Hint.Title>
            </Hint.Content>
          </Hint.Root>
        </div>
      )
    }

    if (!selectedURL) {
      return (
        <div
          className={classNames(
            "external-product-dimensioning",
            "external-product-dimensioning--product-select",
            className
          )}
        >
          <div className="external-product-dimensioning__select">
            <div className="external-product-dimensioning__text">
              {t("Product.Configuration.ChooseDimensioningBase")}
            </div>
            <div className="external-product-dimensioning__select-buttons">
              {externalProductDimensioningURLs.map(dimInfo => (
                <button
                  type="button"
                  className="portfolio-card-button"
                  key={dimInfo.label}
                  onClick={() => setSelectedURL(dimInfo.url)}
                >
                  {renderBaseProduct?.(dimInfo)}
                </button>
              ))}
            </div>
          </div>
        </div>
      )
    }

    return (
      <div className={classNames("external-product-dimensioning", className)}>
        <div className="external-product-dimensioning__calculator card card--elevated">
          {isLoading ? (
            <div className="external-product-dimensioning__iframe-loader">
              <LoadingSpinner />
            </div>
          ) : null}

          <iframe
            id="external-product-dimensioning-frame"
            className="external-product-dimensioning__iframe"
            title={t("Product.Configuration.Dimensioning")}
            src={selectedURL}
            onLoad={() => setIsLoading(false)}
          />
        </div>
      </div>
    )
  }
)
